// MembersPanel — the account-membership admin surface (ACL P6, marketplace-
// account-acl design §2/§2.5/§RULING T-1; ⚠️ REWRITTEN 2026-10-04,
// account-area redesign Task 11 per the plan's 2026-10-02 amendment F22).
//
// ORIGINAL: three stacked cards (member list with inline-confirm remove, an
// inline invite form, pending invitations with link actions) plus an
// owner-only transfer card. CURRENT (F22 — "use tabs" for pages mixing
// kinds of information): the Task 4 registry's THREE TABS rendered with the
// unmodified EmbedTabs —
//   Members     : the members table (Person / Role badge / Status). The
//                 owner row and the caller's own row render NO remove
//                 affordance; a removable row's EmbedActionMenu offers
//                 "Remove <label>" behind an alertdialog.
//   Invitations : the invitations table, with the "Invite member" action in
//                 THIS tab's header (F22's own ruling) opening the invite
//                 dialog; rows offer Resend (immediate) and Revoke (behind
//                 an alertdialog).
//   Ownership   : owner-only via the registry's visibleWhen (sv.role ===
//                 "owner" — the Task-4 registry row, re-derived here through
//                 visibleTabs); "Transfer…" opens the transfer dialog.
// The tab ids/labels come from account-sections.ts SECTION_TABS.members (the
// single vocabulary — never re-stringed here); selection is React state only
// (rule 7: never storage), clamped against the visible list like
// ProfilePanel's post-transfer demotion guard.
//
// Kept verbatim from the pre-tab panel, re-scoped to its tab:
//   - The section gate stays manage-membership and the fetch behaviour is
//     unchanged: ONE allSettled pair (listMembers + listInvitations) at
//     mount; a failed invitations read never takes the member list down.
//   - Leave is NOT here (ruling R2: it lives at the bottom of Profile &
//     security's Profile tab — LeaveAccountBlock).
//   - No "Change role" action exists anywhere: there is no API for it.
//   - Invite dialog: offered role is manager|user|guest, NEVER owner (the
//     DDL check); the §2.5 exclusivity refusals surface VERBATIM — the
//     server message is the contract, no client-side reinterpretation.
//   - §59: every list mutation seeds from the MUTATION RESPONSE — the
//     created invitation (server-assigned id/expiry) and the resend's FRESH
//     row (revoke+recreate: new id + expiry, never an in-place mutation).
//   - The owner-target remove refusal ("The account owner cannot leave or
//     be removed — transfer ownership first") surfaces VERBATIM: the server
//     refusal remains the control (§3.3) — this panel hides when denied, it
//     never enforces.
//   - Transfer: the picker shows human-readable labels and submits the
//     NUMERIC user_id (rule 66: label in the UI, id on the wire). The
//     dialog names the irreversibility. Success MUST re-seed self-view via
//     onSelfViewStale — the caller's role just changed owner→manager, which
//     typically hides this very section.
//   - TD-476 (the 49-char member email at 390px): the minWidth:0 +
//     overflowWrap:anywhere pair is retained — now owned CENTRALLY by
//     EmbedTable's cellText, applied to every cell in both the wide table
//     and the stacked list (MembersPanel.test.tsx + the TD-476 smoke pin
//     the effect; the primitive is where the properties live).
//
// DIVERGENCE (see membership-api.ts): the members wire carries no
// name/email — the wire's email label (post-review backend join) renders
// when present; the "Member #<user_id>" id fallback covers the
// soft-deleted-user shape. The transfer picker's labels are the same
// best-available form.
//
// Inline styles only — no Tailwind (embed convention). Every rendered
// <button> carries an explicit type (project rule §73 — EmbedButton and the
// primitives do it at birth). All mutations are async/await (rule 8).

import { useState, useEffect, useCallback, useRef, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedText,
  EmbedButton,
  EmbedBanner,
  EmbedField,
  EmbedInput,
  EmbedSelect,
  EmbedTabs,
  EmbedTable,
  EmbedStatusBadge,
  EmbedEmptyState,
  EmbedActionMenu,
  EmbedDialog,
  useEmbedDialog,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { formatInstantDate } from "../../embed-shared/format";
import { visibleTabs } from "./account-sections";
import {
  listMembers,
  removeMember,
  listInvitations,
  createInvitation,
  revokeInvitation,
  resendInvitation,
  transferOwnership,
  type OfferedRole,
  type MembershipWire,
  type InvitationWire,
  type SelfViewWire,
} from "./membership-api";

/** The member row's human label (rule 66: label in the UI, user_id on the
 *  wire). The members-list projection carries the member's EMAIL (the
 *  backend's LEFT JOIN — post-P6 review); ''/absent is the soft-deleted-user
 *  shape, where the stable id fallback is what renders. The role is NOT part
 *  of the label any more — the Role badge is its own column (F22 table). */
function memberLabel(m: MembershipWire): string {
  return m.email || `Member #${m.user_id}`;
}

interface InviteFields {
  email: string;
  role: OfferedRole;
}

const EMPTY_INVITE: InviteFields = { email: "", role: "manager" };

export interface MembersPanelProps {
  /** The caller's (AccountCenter's) self-view snapshot — drives the tab
   *  visibility (the registry's owner-only Ownership tab), the "(you)"
   *  marker and the remove/transfer affordances. May be null while loading
   *  or on a fetch failure; the panel then simply omits the owner-only
   *  affordances (fail-closed presentation). */
  selfView: SelfViewWire | null;
  /** Fired after a successful ownership transfer — the caller's role changed
   *  (owner → manager), so the owner must re-read self-view (plan §1.5);
   *  AccountCenter's re-fetch re-derives the section gating from it. */
  onSelfViewStale: () => void;
}

export function MembersPanel({ selfView, onSelfViewStale }: MembersPanelProps) {
  const t = useEmbedTokens();

  // ---- Tabs (F22). The registry is the single tab vocabulary; selection is
  // React state only (rule 7). requestedTab is CLAMPED against the currently
  // visible list (the ProfilePanel idiom): a self-view reload that hides the
  // tab the member is on (the post-transfer demotion) falls back to the
  // first visible tab instead of leaving EmbedTabs' value pointing at a tab
  // that no longer renders. ----
  const tabs = visibleTabs("members", selfView);
  const [requestedTab, setRequestedTab] = useState<string | null>(null);
  const activeTabId = tabs.some((tab) => tab.id === requestedTab)
    ? (requestedTab as string)
    : (tabs[0]?.id ?? "members");

  const [members, setMembers] = useState<MembershipWire[]>([]);
  const [invitations, setInvitations] = useState<InvitationWire[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Invite dialog. The form's state lives HERE, not inside the dialog (the
  // MidsPanel convention): EmbedDialog unmounts its children on close, and a
  // customer who cancels (or fat-fingers Escape) must find their half-typed
  // draft still there when they reopen. Success clears it.
  const inviteDialog = useEmbedDialog();
  const [fields, setFields] = useState<InviteFields>(EMPTY_INVITE);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  // Remove confirm (alertdialog). The target row is panel state so the
  // dialog can name the person and the confirm can act on the wire id.
  const removeDialog = useEmbedDialog();
  const [removeTarget, setRemoveTarget] = useState<MembershipWire | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  // Resend is immediate (no confirm): its refusal is the Invitations tab's
  // page-level banner (role=alert), never a dialog.
  const [resendError, setResendError] = useState<string | null>(null);

  // Revoke confirm (alertdialog).
  const revokeDialog = useEmbedDialog();
  const [revokeTarget, setRevokeTarget] = useState<InvitationWire | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Transfer dialog (Ownership tab, owner-only via the registry).
  const transferDialog = useEmbedDialog();
  const [successorChoice, setSuccessorChoice] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  // Rendered at PANEL level, above the tabs: the post-transfer re-fetch
  // typically unmounts this whole section, and while it stays mounted (the
  // widened-manager shape) the message must not vanish on a tab switch.
  const [transferDone, setTransferDone] = useState<string | null>(null);

  const messageOf = useCallback((reason: unknown, fallback: string): string => {
    // VERBATIM contract: an ApiError's message is the server's refusal text —
    // the exclusivity/owner/404 messages are the UX. Only non-ApiError
    // failures (network) fall back to a generic line.
    return reason instanceof ApiError ? reason.message : fallback;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // allSettled (the MidsPanel convention): a failed invitations read must
    // not take the member list down with it — the two lists back independent
    // tabs of the panel. Fetch behaviour unchanged from the pre-tab panel.
    const [membersR, invitationsR] = await Promise.allSettled([listMembers(), listInvitations()]);
    if (membersR.status === "fulfilled") setMembers(membersR.value);
    else setLoadError(messageOf(membersR.reason, "Failed to load members"));
    if (invitationsR.status === "fulfilled") setInvitations(invitationsR.value);
    setLoading(false);
  }, [messageOf]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Invite ----

  const openInvite = useCallback(
    (trigger: HTMLElement | null) => {
      setInviteError(null);
      setInviteSuccess(null);
      inviteDialog.openFrom(trigger);
    },
    [inviteDialog],
  );

  const handleInvite = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const email = fields.email.trim();
      if (!email) {
        setInviteError("An email address is required.");
        return;
      }
      setInviting(true);
      setInviteError(null);
      try {
        // §59: seed the pending list from the MUTATION RESPONSE — the server
        // assigned id/expiry; a local construction could only guess at them.
        const created = await createInvitation(email, fields.role);
        setInvitations((prev) => [...prev, created]);
        setFields(EMPTY_INVITE);
        setInviteSuccess(`Invitation sent to ${created.invitee_email}.`);
        inviteDialog.close();
      } catch (err) {
        // 409 INVITEE_ALREADY_MEMBER / 409 INVITATION_CONFLICT / 400 — the
        // server message renders verbatim INSIDE the dialog (the §2.5
        // exclusivity contract), values intact so the customer can correct.
        setInviteError(messageOf(err, "Failed to send the invitation. Please try again."));
      } finally {
        setInviting(false);
      }
    },
    [fields, inviteDialog, messageOf],
  );

  // Review P2 (the MidsPanel precedent): when the invitations list is EMPTY
  // the tab header's band button yields — the empty state's action is the
  // only "Invite member" trigger — and a first-ever success unmounts that
  // empty state in the same React commit that closes the dialog, detaching
  // EmbedDialog's focus-restore target. The band's button mounts in that
  // same commit: focus it from this post-commit effect. When the band's own
  // button opened the dialog it is still connected and EmbedDialog's restore
  // already handled it — the detached check leaves that path alone.
  const inviteBandRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (inviteSuccess && inviteDialog.returnFocusTo !== null && !inviteDialog.returnFocusTo.isConnected) {
      inviteBandRef.current?.querySelector("button")?.focus();
    }
  }, [inviteSuccess, inviteDialog.returnFocusTo]);

  // ---- Remove (Members tab) ----

  // The owner row and the caller's own row have NO remove affordance at all:
  // the Step-1 pin. The server's owner-check remains the control (§3.3).
  const canRemove = useCallback(
    (m: MembershipWire) => m.role !== "owner" && m.user_id !== ownUserIdOf(selfView),
    [selfView],
  );

  const openRemove = useCallback(
    (m: MembershipWire, trigger: HTMLElement | null) => {
      setRemoveTarget(m);
      setRemoveError(null);
      removeDialog.openFrom(trigger);
    },
    [removeDialog],
  );

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeMember(removeTarget.user_id);
      const removedId = removeTarget.user_id;
      setRemoveTarget(null);
      removeDialog.close();
      setMembers((prev) => prev.filter((m) => m.user_id !== removedId));
    } catch (err) {
      // Owner-target refusal ("The account owner cannot leave or be
      // removed — transfer ownership first") and the uniform 404 surface
      // verbatim INSIDE the dialog — the row and the dialog stay.
      setRemoveError(messageOf(err, "Failed to remove this member."));
    } finally {
      setRemoving(false);
    }
  }, [removeDialog, removeTarget, messageOf]);

  // Review P2 (the invite fallback's own shape, on the remove SUCCESS path):
  // the confirm filters the member out in the same React commit that closes
  // the dialog, so the row — carrying the ⋯ trigger openRemove stored as the
  // focus-restore target — unmounts and EmbedDialog's restore skips the
  // detached target: focus drops to <body> on EVERY removal. There is no
  // remove-success state to key on, but a close whose stored trigger is
  // detached IS exactly that commit — a cancel leaves the row (and the
  // trigger) mounted, so this stays inert there. Focus the action trigger of
  // the row that now occupies the removed row's place; only the active tab's
  // table is mounted, and with no removable row left the active tab is the
  // panel's stable focusable target.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!removeDialog.open && removeDialog.returnFocusTo !== null && !removeDialog.returnFocusTo.isConnected) {
      (
        panelRef.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]') ??
        panelRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
      )?.focus();
    }
  }, [removeDialog.open, removeDialog.returnFocusTo]);

  // ---- Resend + revoke (Invitations tab) ----

  const handleResend = useCallback(
    async (inv: InvitationWire) => {
      setResendError(null);
      try {
        // Resend is revoke+recreate: the FRESH row (new id + expiry) seeds
        // the list — §59 against the mutation response, never an in-place
        // mutation of the old row.
        const fresh = await resendInvitation(inv.id);
        setInvitations((prev) => prev.map((i) => (i.id === inv.id ? fresh : i)));
      } catch (err) {
        // 404 "No such pending invitation" (already revoked/consumed)
        // surfaces verbatim — the customer learns the row is gone.
        setResendError(messageOf(err, "Failed to resend this invitation."));
      }
    },
    [messageOf],
  );

  const openRevoke = useCallback(
    (inv: InvitationWire, trigger: HTMLElement | null) => {
      setRevokeTarget(inv);
      setRevokeError(null);
      revokeDialog.openFrom(trigger);
    },
    [revokeDialog],
  );

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await revokeInvitation(revokeTarget.id);
      const revokedId = revokeTarget.id;
      setRevokeTarget(null);
      revokeDialog.close();
      // The list is pending-only; a revoked row leaves it (the response's
      // revoked_at has no display meaning on a pending list).
      setInvitations((prev) => prev.filter((i) => i.id !== revokedId));
    } catch (err) {
      setRevokeError(messageOf(err, "Failed to revoke this invitation."));
    } finally {
      setRevoking(false);
    }
  }, [revokeDialog, revokeTarget, messageOf]);

  // Review P2 (the remove fallback above, on the revoke SUCCESS path): the
  // revoked row — carrying the ⋯ trigger openRevoke stored — unmounts in the
  // same commit that closes the dialog, so the same detached-trigger rescue
  // applies: focus the next remaining invitation row's trigger, else the
  // active tab (revoking the last invitation leaves no rows at all).
  useEffect(() => {
    if (!revokeDialog.open && revokeDialog.returnFocusTo !== null && !revokeDialog.returnFocusTo.isConnected) {
      (
        panelRef.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]') ??
        panelRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
      )?.focus();
    }
  }, [revokeDialog.open, revokeDialog.returnFocusTo]);

  // ---- Transfer (Ownership tab) ----

  const openTransfer = useCallback(
    (trigger: HTMLElement | null) => {
      setSuccessorChoice("");
      setTransferError(null);
      transferDialog.openFrom(trigger);
    },
    [transferDialog],
  );

  const handleConfirmTransfer = useCallback(async () => {
    if (!chosenSuccessorOf(members, selfView, successorChoice)) return;
    const successorUserId = Number(successorChoice);
    setTransferring(true);
    setTransferError(null);
    try {
      await transferOwnership(successorUserId);
      setSuccessorChoice("");
      transferDialog.close();
      // The role just changed owner→manager: the caller MUST re-read
      // self-view (plan §1.5). AccountCenter's re-fetch re-derives the
      // section gating — which typically hides this panel entirely.
      setTransferDone("Ownership transferred. You are now a manager of this account.");
      onSelfViewStale();
    } catch (err) {
      setTransferError(messageOf(err, "Failed to transfer ownership."));
    } finally {
      setTransferring(false);
    }
  }, [members, selfView, successorChoice, transferDialog, messageOf, onSelfViewStale]);

  // Owner-only affordances: keyed on self-view's role — the server's P7
  // owner check is the control; this only decides whether the tab renders
  // (the registry's visibleWhen owns the same rule; a null self-view omits
  // it — fail-closed presentation).
  const isOwner = selfView?.role === "owner";
  const ownUserId = selfView?.membership?.user_id ?? null;

  const successorOptions = members.filter(
    (m) => m.role !== "owner" && m.user_id !== ownUserId,
  );
  const chosenSuccessor = successorOptions.find(
    (m) => String(m.user_id) === successorChoice,
  );

  const inviteBandButton = (variant: "primary") => (
    <EmbedButton
      type="button"
      variant={variant}
      onClick={(e) => openInvite(e.currentTarget)}
    >
      Invite member
    </EmbedButton>
  );

  return (
    <div ref={panelRef} style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      {transferDone && <EmbedBanner tone="success">{transferDone}</EmbedBanner>}

      {/* Labels live in the Task 4 registry (SECTION_TABS.members) — this
          call site renders whatever visibleTabs answers for THIS self-view
          (the owner-only Ownership tab drops out for everyone else). */}
      <EmbedTabs
        aria-label="Members sections"
        items={tabs.map((tab) => ({ id: tab.id, label: tab.label }))}
        value={activeTabId}
        onChange={setRequestedTab}
      />

      {activeTabId === "members" && (
        <>
          {loadError && (
            <EmbedCard>
              {/* EmbedBanner(error) already carries role="alert" — no
                  wrapper role here (a second one makes getByRole("alert")
                  ambiguous). */}
              <EmbedBanner tone="error">{loadError}</EmbedBanner>
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedButton type="button" variant="secondary" onClick={load}>
                  Retry
                </EmbedButton>
              </div>
            </EmbedCard>
          )}

          {loading && (
            <p role="status" style={{ margin: 0 }}>
              <EmbedText tone="secondary">Loading members...</EmbedText>
            </p>
          )}

          {!loading && !loadError && members.length === 0 && (
            <EmbedEmptyState
              icon="members"
              title="No members yet."
              body="People with access to this account will appear here."
            />
          )}

          {!loading && members.length > 0 && (
            <EmbedTable<MembershipWire>
              caption="Account members"
              rows={members}
              rowKey={(m) => String(m.user_id)}
              rowTestId={(m) => `member-row-${m.user_id}`}
              columns={[
                {
                  id: "person",
                  header: "Person",
                  primary: true,
                  cell: (m) => (
                    // TD-476 lives in EmbedTable's cellText (minWidth:0 +
                    // overflowWrap:anywhere on every cell) — the long-email
                    // pin is proven against the primitive by the TD-476 smoke.
                    <span style={{ fontWeight: t.fontWeight.medium }}>
                      {memberLabel(m)}
                      {m.user_id === ownUserId && (
                        <span style={{ fontWeight: t.fontWeight.regular, color: t.colors.textSecondary }}>
                          {" "}(you)
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: "role",
                  header: "Role",
                  cell: (m) => <EmbedStatusBadge domain="member" value={m.role} />,
                },
                {
                  id: "status",
                  header: "Status",
                  cell: (m) => (
                    // joined_at is the application-level join instant
                    // (TIMESTAMPTZ) → formatInstantDate, the viewer's local
                    // date (ruling R1's split: instants local, DATEs UTC).
                    <span>Active since {formatInstantDate(m.joined_at)}</span>
                  ),
                },
                {
                  id: "actions",
                  header: "",
                  stackedLabel: false,
                  cell: (m) =>
                    canRemove(m) ? (
                      <EmbedActionMenu
                        label={`Actions for ${memberLabel(m)}`}
                        items={[
                          {
                            id: "remove",
                            label: `Remove ${memberLabel(m)}`,
                            tone: "danger",
                            onSelect: (trigger) => openRemove(m, trigger),
                          },
                        ]}
                      />
                    ) : null,
                },
              ]}
            />
          )}
        </>
      )}

      {activeTabId === "invitations" && (
        <>
          {/* The tab's action band (F22: the invite action lives in THIS
              tab's header). While the empty state shows, the band's button
              yields — the empty state's action is the only trigger. */}
          <div
            ref={inviteBandRef}
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: t.spacing.md,
            }}
          >
            <p
              style={{
                margin: 0,
                minWidth: 0,
                maxWidth: 640,
                color: t.colors.textSecondary,
                fontSize: t.fontSize.sm,
                lineHeight: t.lineHeight.normal,
              }}
            >
              People invited to join this account appear here until they accept.
            </p>
            {invitations.length > 0 && inviteBandButton("primary")}
          </div>

          {inviteSuccess && (
            // EmbedBanner(success) carries role="status" (polite) — the
            // banner lives at tab level because the dialog closes on success.
            <EmbedBanner tone="success">{inviteSuccess}</EmbedBanner>
          )}

          {resendError && (
            <EmbedBanner tone="error">{resendError}</EmbedBanner>
          )}

          {!loading && invitations.length === 0 && (
            <EmbedEmptyState
              icon="members"
              title="No pending invitations."
              body="Invite someone and their invitation will appear here until they accept."
              action={inviteBandButton("primary")}
            />
          )}

          {!loading && invitations.length > 0 && (
            <EmbedTable<InvitationWire>
              caption="Pending invitations"
              rows={invitations}
              rowKey={(inv) => inv.id}
              rowTestId={(inv) => `invitation-row-${inv.id}`}
              columns={[
                {
                  id: "person",
                  header: "Person",
                  primary: true,
                  cell: (inv) => (
                    <span style={{ fontWeight: t.fontWeight.medium }}>{inv.invitee_email}</span>
                  ),
                },
                {
                  id: "role",
                  header: "Role",
                  // The OFFERED role — the same manager/user/guest vocabulary
                  // the member domain maps (owner is not invitable).
                  cell: (inv) => <EmbedStatusBadge domain="member" value={inv.offered_role} />,
                },
                {
                  id: "status",
                  header: "Status",
                  cell: (inv) => (
                    // The list is pending-only; the expiry is the one date
                    // that matters here. Year omitted — it is always near.
                    <span>
                      Invited · expires {formatInstantDate(inv.expires_at, { withYear: false })}
                    </span>
                  ),
                },
                {
                  id: "actions",
                  header: "",
                  stackedLabel: false,
                  cell: (inv) => (
                    <EmbedActionMenu
                      label={`Actions for ${inv.invitee_email}`}
                      items={[
                        { id: "resend", label: "Resend", onSelect: () => void handleResend(inv) },
                        {
                          id: "revoke",
                          label: "Revoke",
                          tone: "danger",
                          onSelect: (trigger) => openRevoke(inv, trigger),
                        },
                      ]}
                    />
                  ),
                },
              ]}
            />
          )}
        </>
      )}

      {activeTabId === "ownership" && isOwner && (
        <>
          <p
            style={{
              margin: 0,
              maxWidth: 640,
              color: t.colors.textSecondary,
              fontSize: t.fontSize.sm,
              lineHeight: t.lineHeight.normal,
            }}
          >
            Hand this account to one of its members. You become a manager and
            the successor becomes the owner — immediate and irreversible.
          </p>
          <div>
            <EmbedButton type="button" variant="danger" onClick={(e) => openTransfer(e.currentTarget)}>
              Transfer…
            </EmbedButton>
          </div>
        </>
      )}

      {/* Invite member — the Invitations tab's action-band dialog. The form's
          state lives in the PANEL (see the invite state above), so a cancel
          leaves the draft in place and a success seeds the table and closes
          in the same commit. Offered roles are manager|user|guest — owner is
          assigned by transfer, never by invitation (the DDL check), so no
          owner option exists here. closeDisabled pins Escape and the backdrop
          while the POST is in flight. */}
      <EmbedDialog
        open={inviteDialog.open}
        onClose={inviteDialog.close}
        returnFocusTo={inviteDialog.returnFocusTo}
        title="Invite member"
        closeDisabled={inviting}
        size="small"
      >
        <form onSubmit={handleInvite} noValidate>
          {inviteError && (
            <div style={{ marginBottom: t.spacing.md }}>
              <EmbedBanner tone="error">{inviteError}</EmbedBanner>
            </div>
          )}
          <EmbedField
            label="Email"
            htmlFor="invite-email"
            required
            help="They'll get an email with a link to accept this invitation."
          >
            <EmbedInput
              id="invite-email"
              type="email"
              value={fields.email}
              onChange={(e) => setFields((f) => ({ ...f, email: e.target.value }))}
              aria-required="true"
            />
          </EmbedField>
          <EmbedField
            label="Role"
            htmlFor="invite-role"
            help="Managers can manage the account; users and guests get their listed permissions only."
          >
            <EmbedSelect
              id="invite-role"
              value={fields.role}
              onChange={(e) => setFields((f) => ({ ...f, role: e.target.value as OfferedRole }))}
            >
              <option value="manager">Manager</option>
              <option value="user">User</option>
              <option value="guest">Guest</option>
            </EmbedSelect>
          </EmbedField>
          <div
            style={{
              marginTop: t.spacing.md,
              display: "flex",
              gap: t.spacing.md,
              justifyContent: "flex-end",
            }}
          >
            <EmbedButton type="button" variant="secondary" onClick={inviteDialog.close} disabled={inviting}>
              Cancel
            </EmbedButton>
            <EmbedButton type="submit" variant="primary" disabled={inviting} loading={inviting}>
              {inviting ? "Sending..." : "Send invitation"}
            </EmbedButton>
          </div>
        </form>
      </EmbedDialog>

      {/* Remove member — the alertdialog names the person before the wire
          call; a refusal keeps it open with the row untouched. closeDisabled
          pins Escape and the backdrop while the POST is in flight. */}
      <EmbedDialog
        open={removeDialog.open}
        onClose={removeDialog.close}
        returnFocusTo={removeDialog.returnFocusTo}
        title="Remove member?"
        role="alertdialog"
        closeDisabled={removing}
        size="small"
      >
        {removeError && (
          <div style={{ marginBottom: t.spacing.md }}>
            <EmbedBanner tone="error">{removeError}</EmbedBanner>
          </div>
        )}
        {removeTarget && (
          <p style={{ margin: 0, color: t.colors.text, fontSize: t.fontSize.sm, lineHeight: t.lineHeight.normal }}>
            Remove {memberLabel(removeTarget)} from this account? They will
            lose access immediately.
          </p>
        )}
        <div
          style={{
            marginTop: t.spacing.md,
            display: "flex",
            gap: t.spacing.md,
            justifyContent: "flex-end",
          }}
        >
          <EmbedButton type="button" variant="secondary" onClick={removeDialog.close} disabled={removing}>
            Cancel
          </EmbedButton>
          <EmbedButton
            type="button"
            variant="danger"
            onClick={handleConfirmRemove}
            disabled={removing}
            loading={removing}
          >
            {removing ? "Removing..." : "Remove member"}
          </EmbedButton>
        </div>
      </EmbedDialog>

      {/* Revoke invitation — same alertdialog shape as remove. */}
      <EmbedDialog
        open={revokeDialog.open}
        onClose={revokeDialog.close}
        returnFocusTo={revokeDialog.returnFocusTo}
        title="Revoke invitation?"
        role="alertdialog"
        closeDisabled={revoking}
        size="small"
      >
        {revokeError && (
          <div style={{ marginBottom: t.spacing.md }}>
            <EmbedBanner tone="error">{revokeError}</EmbedBanner>
          </div>
        )}
        {revokeTarget && (
          <p style={{ margin: 0, color: t.colors.text, fontSize: t.fontSize.sm, lineHeight: t.lineHeight.normal }}>
            Revoke the invitation for {revokeTarget.invitee_email}? They will
            not be able to accept it.
          </p>
        )}
        <div
          style={{
            marginTop: t.spacing.md,
            display: "flex",
            gap: t.spacing.md,
            justifyContent: "flex-end",
          }}
        >
          <EmbedButton type="button" variant="secondary" onClick={revokeDialog.close} disabled={revoking}>
            Cancel
          </EmbedButton>
          <EmbedButton
            type="button"
            variant="danger"
            onClick={handleConfirmRevoke}
            disabled={revoking}
            loading={revoking}
          >
            {revoking ? "Revoking..." : "Revoke invitation"}
          </EmbedButton>
        </div>
      </EmbedDialog>

      {/* Transfer ownership — the dialog owns none of the picker's state on
          a cancel (it resets on OPEN, not on close); a refusal renders
          verbatim inside with the choice intact. */}
      <EmbedDialog
        open={transferDialog.open}
        onClose={transferDialog.close}
        returnFocusTo={transferDialog.returnFocusTo}
        title="Transfer ownership"
        closeDisabled={transferring}
        size="small"
      >
        {transferError && (
          <div style={{ marginBottom: t.spacing.md }}>
            <EmbedBanner tone="error">{transferError}</EmbedBanner>
          </div>
        )}
        <EmbedField
          label="Successor"
          htmlFor="transfer-successor"
          help={
            successorOptions.length === 0
              ? "Invite a member first — ownership can only transfer to a live member of this account."
              : undefined
          }
        >
          <EmbedSelect
            id="transfer-successor"
            value={successorChoice}
            onChange={(e) => setSuccessorChoice(e.target.value)}
            disabled={successorOptions.length === 0}
          >
            <option value="">Choose a member…</option>
            {/* Rule 66: human labels in the UI, the NUMERIC user_id is
                the submitted value — never a raw-id input. */}
            {successorOptions.map((m) => (
              <option key={m.user_id} value={String(m.user_id)}>
                {memberLabel(m)}
              </option>
            ))}
          </EmbedSelect>
        </EmbedField>
        {chosenSuccessor && (
          <p style={{ margin: `${t.spacing.md}px 0 0`, color: t.colors.text, fontSize: t.fontSize.sm, lineHeight: t.lineHeight.normal }}>
            Transfer ownership to {memberLabel(chosenSuccessor)}? You will
            cease to be the owner and cannot undo this —{" "}
            {memberLabel(chosenSuccessor)} gains full control of the account.
          </p>
        )}
        <div
          style={{
            marginTop: t.spacing.md,
            display: "flex",
            gap: t.spacing.md,
            justifyContent: "flex-end",
          }}
        >
          <EmbedButton type="button" variant="secondary" onClick={transferDialog.close} disabled={transferring}>
            Cancel
          </EmbedButton>
          <EmbedButton
            type="button"
            variant="danger"
            onClick={handleConfirmTransfer}
            disabled={!chosenSuccessor || transferring}
            loading={transferring}
          >
            {transferring ? "Transferring..." : "Transfer ownership"}
          </EmbedButton>
        </div>
      </EmbedDialog>
    </div>
  );
}

function ownUserIdOf(selfView: SelfViewWire | null): number | null {
  return selfView?.membership?.user_id ?? null;
}

function chosenSuccessorOf(
  members: MembershipWire[],
  selfView: SelfViewWire | null,
  successorChoice: string,
): MembershipWire | undefined {
  const ownUserId = ownUserIdOf(selfView);
  return members
    .filter((m) => m.role !== "owner" && m.user_id !== ownUserId)
    .find((m) => String(m.user_id) === successorChoice);
}
